import type {
  NegociacaoCasoTelefoneUi,
  NegociacaoTelefoneStatusUi,
} from '../types/negociacaoDetailTypes';

/** Só três variantes, como no design: brand para o ativo, error para falha, neutral no resto. */
export interface TelefoneTag {
  label: string;
  variant: 'brand' | 'neutral' | 'error';
  /** Texto do ⓘ. Ausente ⇒ a tag não mostra o ícone de ajuda. */
  ajuda?: string;
}

// Textos do glossário do design (nó 28484:189), copiados ao pé da letra: são a cópia de produto
// dos tooltips, não paráfrase. Toda explicação do glossário vira tooltip na tela real.
const AJUDA_ALTERNATIVA = 'Será acionada se necessário, caso as tentativas anteriores falhem.';
const AJUDA_SUPRIMIDO = 'Esse número teve uma tentativa de entrega sem sucesso antes.';
const AJUDA_CELULAR_INVALIDO = 'Esse número não foi encontrado.';
const AJUDA_SEM_WHATSAPP = 'Esse número ainda não tem WhatsApp.';
const AJUDA_RETORNO_TARDIO = 'Respondeu depois que a negociação já seguia por outro número.';

/**
 * O devedor respondeu DEPOIS que este número saiu de cena.
 *
 * `esgotadoEm` é o instante em que a fila desistiu dele e seguiu para o próximo; um inbound
 * posterior a isso chegou num canal que já não negocia mais. Sem inbound, ou sem data de
 * esgotamento, não há retorno tardio — é só "Sem resposta".
 */
const respondeuDepoisDeSair = (telefone: NegociacaoCasoTelefoneUi): boolean => {
  if (!telefone.ultimoInboundEm || !telefone.esgotadoEm) return false;
  return new Date(telefone.ultimoInboundEm).getTime() > new Date(telefone.esgotadoEm).getTime();
};

/**
 * A tag exibida ao lado de cada número da fila do caso.
 *
 * Função PURA e sem componente novo: reusa o `Badge` compartilhado em vez de criar um sexto
 * pill de status. É aqui que o sinal de regressão está — a matriz de tags é a única lógica da
 * feature no front, e testá-la não precisa de render.
 *
 * **`Não verificado` e `Incerto` não ganham representação visual própria** (spec da tela): o
 * número recebe apenas a tag real de status, sem ícone, cor ou selo adicional.
 *
 * As tags de linha são todas NEUTRAS no design — a cor fica reservada ao canal ativo (brand) e
 * à falha da conversa (error). Colorir cada linha por status transformaria a lista num
 * semáforo, que é justamente o que o design evita.
 */
export const resolveTelefoneTag = (telefone: NegociacaoCasoTelefoneUi): TelefoneTag | null => {
  switch (telefone.status) {
    case 'NEGOCIANDO':
      return { label: 'Negociando', variant: 'brand' };
    case 'NA_FILA':
      // Número que ainda não foi tentado — é a próxima alternativa da fila.
      return { label: 'Alternativa', variant: 'neutral', ajuda: AJUDA_ALTERNATIVA };
    case 'BARRADO':
      return resolveTagBarrado(telefone.verificadoDetalhe);
    case 'DESCARTADO':
      // Só "Não é a pessoa" tem entrada no glossário (visual neutro, igual a "Sem resposta").
      // `NUMERO_INCORRETO_INFORMADO` vem do começo do flow de ingestão e **não é mapeado** por
      // enquanto — decisão do Diego (2026-08-25): sem tag é melhor que tag inventada.
      return telefone.descartadoMotivo === 'NEGOU_IDENTIDADE'
        ? { label: 'Não é a pessoa', variant: 'neutral' }
        : null;
    case 'ESGOTADO':
      // Esgotou sem resposta é "Sem resposta"; se veio inbound depois de a fila seguir em
      // frente, o mesmo número passa a ser "Retorno tardio".
      return respondeuDepoisDeSair(telefone)
        ? { label: 'Retorno tardio', variant: 'neutral', ajuda: AJUDA_RETORNO_TARDIO }
        : { label: 'Sem resposta', variant: 'neutral' };
    case 'FALHA_TECNICA':
      // Falha técnica é falha da conversa para quem lê a tela — mesmo rótulo, mesma cor de
      // destaque. O detalhe técnico do motivo não vira vocabulário de produto.
      return { label: 'Falha na conversa', variant: 'error' };
    default:
      return null;
  }
};

/**
 * O motivo do porteiro vem como texto do kernel (`número bloqueado por falha anterior`,
 * `número de celular inválido`, `número sem WhatsApp ativo`). A tela mostra o rótulo curto de
 * produto, então a tradução é por conteúdo do detalhe — e o fallback é o rótulo genérico, nunca
 * o texto cru do kernel, que não foi escrito para ser lido pelo cliente.
 *
 * Os três rótulos são os do glossário (28484:189), origem "Validação (bloqueio)":
 * `Suprimido`, `Celular inválido` e `Sem WhatsApp`. Nenhum deles é "Retorno tardio" — esse é
 * estado da CONVERSA (responder num canal que a fila já abandonou), não veredito de validação.
 *
 * `Celular inválido` está implementado mas é inalcançável hoje: ele exige `validMobile = NAO`,
 * que só vem do HLR do Infobip (desligado) — o `erroCodigo 110` do cpfcnpj cai em "sem WhatsApp".
 * Fica no mapa porque é a tradução correta se o HLR for ligado.
 */
const resolveTagBarrado = (detalhe: string | null): TelefoneTag => {
  const texto = (detalhe ?? '').toLowerCase();
  if (texto.includes('falha anterior') || texto.includes('suprimido')) {
    return { label: 'Suprimido', variant: 'neutral', ajuda: AJUDA_SUPRIMIDO };
  }
  if (texto.includes('celular inválido') || texto.includes('celular invalido')) {
    return { label: 'Celular inválido', variant: 'neutral', ajuda: AJUDA_CELULAR_INVALIDO };
  }
  return { label: 'Sem WhatsApp', variant: 'neutral', ajuda: AJUDA_SEM_WHATSAPP };
};

/**
 * A tag do canal exibido em destaque (painel lateral e barra do chat).
 *
 * Enquanto existe número em negociação, é `Negociando`. Esgotada a fila, nenhum número está
 * NEGOCIANDO e o canal em destaque passa a mostrar **`Falha na conversa`** — é o estado
 * "todos os canais sem sucesso" do design.
 */
export const resolveTagDoCanalAtivo = (
  telefones: NegociacaoCasoTelefoneUi[],
): TelefoneTag | null => {
  if (telefones.length === 0) return null;

  const ativo = encontrarNumeroAtivo(telefones);
  if (ativo) return { label: 'Negociando', variant: 'brand' };

  const tentados = telefones.some((telefone) => telefone.tentativas > 0);
  return tentados ? { label: 'Falha na conversa', variant: 'error' } : null;
};

/** O número em negociação agora. `null` em caso legado (sem fila) ou fila esgotada. */
export const encontrarNumeroAtivo = (
  telefones: NegociacaoCasoTelefoneUi[],
): NegociacaoCasoTelefoneUi | null =>
  telefones.find((telefone) => telefone.status === 'NEGOCIANDO') ?? null;

/** Os outros números do caso, em ordem — o que o link "+N canais" abre. */
export const listarCanaisAlternativos = (
  telefones: NegociacaoCasoTelefoneUi[],
): NegociacaoCasoTelefoneUi[] => telefones.filter((telefone) => telefone.status !== 'NEGOCIANDO');

/** Status que a aba Chat não oferece. Ver `listarCanaisDoChat`. */
const FORA_DO_CHAT: ReadonlySet<NegociacaoTelefoneStatusUi> = new Set([
  // Nunca acionado: regra do glossário (`Alternativa` é só do painel lateral) e decisão do
  // Diego (2026-08-25) — sem tentativa não há conversa nem estado que a aba saiba contar.
  'NA_FILA',
  // Descartado: o titular NEGOU ser o devedor, ou informou que o número está errado. Oferecer
  // o canal convida a mandar mensagem para quem já disse "não sou eu" (SEC-02, PR #927).
  'DESCARTADO',
]);

/**
 * Os números que a aba Chat pode listar.
 *
 * Barrado pelo porteiro continua na lista: ele tem um estado a comunicar (`Suprimido`,
 * `Sem WhatsApp`) e ninguém foi importunado por ele.
 */
export const listarCanaisDoChat = (
  telefones: NegociacaoCasoTelefoneUi[],
): NegociacaoCasoTelefoneUi[] => telefones.filter((telefone) => !FORA_DO_CHAT.has(telefone.status));
